/**
 * ScrollManager — scroll handling for the NON-morph navigation paths.
 *
 * The shared-element morph (lib/morph.ts) resets/restores scroll itself, inside
 * the View Transition update callback, so the animation stays within the
 * viewport (issue #102). This component covers every *other* path — reduced
 * motion, browsers without the View Transitions API, and browser back/forward —
 * reproducing the same behaviour with plain effects:
 *   - navigating to the overview ("/") restores the scroll position the user
 *     left it at ("never lose your place", DESIGN.md);
 *   - navigating anywhere else lands at the top (fixes the pre-existing
 *     mid-page-landing bug for all navigation, not just the morph path).
 *
 * It NO-OPs while a morph is in flight (`isMorphing()`) so it never fights the
 * VT-driven scroll, and uses `useLayoutEffect` so the scroll lands before paint
 * (no visible jump). Renders nothing.
 */
import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  getSavedOverviewScrollY,
  isMorphing,
  persistOverviewScrollY,
  restoreScroll,
  saveOverviewScrollY,
  scrollToTop,
  setOverviewScrollY,
} from "@/lib/morph";

/** Idle gap after the last scroll event before the offset is persisted to
 *  sessionStorage. The in-memory value updates on every event; only the write is
 *  deferred, so a burst of scroll events collapses to a single write (#104). */
const PERSIST_DEBOUNCE_MS = 120;

export function ScrollManager() {
  const { pathname } = useLocation();

  // Own scroll restoration outright: with the browser's native restoration left
  // on "auto", a back/forward (popstate) landing races our effect and the
  // browser's remembered offset can win — dumping the user mid-page on a forward
  // to a detail screen. "manual" hands full control here, so every path lands
  // deterministically (top forward / banked position back). Restored on unmount.
  useEffect(() => {
    if (typeof history === "undefined" || !("scrollRestoration" in history)) return;
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);

  // Continuously remember the overview's scroll so a later back-navigation — on
  // any path — can restore it. The in-memory module state (which survives the
  // overview unmounting on the way to a detail page) updates eagerly on every
  // event; the sessionStorage write that lets it also survive a hard reload
  // (#104) is trailing-debounced so a scroll gesture collapses to one write, not
  // ~60/s. The morph's forward path banks this eagerly in `toDetail`; the
  // listener covers the plain paths. Layout-effect cleanup removes it before
  // destination scroll/layout changes can overwrite the departing position.
  useLayoutEffect(() => {
    if (pathname !== "/") return;
    // NB: attach only — do NOT record the current scroll on mount. Arriving at
    // the overview via a back-navigation restores scroll in the layout effect
    // below (or, on the morph path, inside the VT callback); recording here
    // would run *before* that restore under flushSync and clobber the banked
    // position with 0.
    let leaving = false;
    let navigationTimer: ReturnType<typeof setTimeout> | undefined;
    const onNavigationClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (
        !(link instanceof HTMLAnchorElement) ||
        link.hasAttribute("download") ||
        (link.target && link.target !== "_self")
      )
        return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.pathname === "/") return;
      // Capture the real offset before the router changes layout, even when a
      // user clicks before the browser has delivered the latest scroll event.
      saveOverviewScrollY(window.scrollY);
      leaving = true;
      if (navigationTimer) clearTimeout(navigationTimer);
      // A later handler may cancel navigation. Resume banking on the next task
      // if this overview remains mounted; real navigation removes the listener.
      navigationTimer = setTimeout(() => {
        leaving = false;
      }, 0);
    };
    let persistTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = () => {
      if (leaving || isMorphing()) return;
      setOverviewScrollY(window.scrollY); // eager, no I/O
      if (persistTimer) clearTimeout(persistTimer);
      persistTimer = setTimeout(persistOverviewScrollY, PERSIST_DEBOUNCE_MS);
    };
    document.addEventListener("click", onNavigationClick, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (navigationTimer) clearTimeout(navigationTimer);
      document.removeEventListener("click", onNavigationClick, true);
      window.removeEventListener("scroll", onScroll);
      // Flush any pending write so the last position survives a reload even if
      // the user navigated away (or reloaded) within the debounce window.
      if (persistTimer) {
        clearTimeout(persistTimer);
        persistOverviewScrollY();
      }
    };
  }, [pathname]);

  useLayoutEffect(() => {
    // The morph drives its own scroll inside the VT callback — don't double-scroll.
    if (isMorphing()) return;
    if (pathname === "/") {
      restoreScroll(getSavedOverviewScrollY());
    } else {
      scrollToTop();
    }
  }, [pathname]);

  return null;
}
